import React from 'react'
import posthog from 'posthog-js'

interface Props {
  children: React.ReactNode
  /** Optional label sent to PostHog to identify which part of the app failed. */
  feature?: string
  /** Optional fallback UI shown when a crash is caught. Defaults to null (nothing). */
  fallback?: React.ReactNode
}

interface State {
  hasError: boolean
}

/**
 * Catches any unhandled React render errors in the subtree and reports them
 * to PostHog as exceptions.  Wrap top-level routes or high-risk feature areas
 * with this component so crashes never show a blank screen silently.
 *
 * Usage:
 *   <ErrorBoundary feature="feed">
 *     <FeedScreen />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    posthog.captureException(error, {
      component_stack: info.componentStack,
      ...(this.props.feature ? { feature: this.props.feature } : {}),
    })
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null
    }
    return this.props.children
  }
}
